import React, { useCallback, useMemo } from 'react'
import { Field } from '@workwhile/ui'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'
import { ModalAsyncSelect } from 'components/ModalSelect'
import { useIndeedFlexCompaniesContextValue } from '../../../context/IndeedFlexCompaniesProvider'

type CompanyOption = { label: string; value: number }

const fuzzyOptions: FullOptions<CompanyOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

type CompanyPickerProps = {
  value?: number | null
  onChange: (value: number | undefined) => void
  error?: string
  label?: React.ReactNode
}

export const CompanyPicker = ({
  value,
  onChange,
  error,
  label = 'Company',
}: CompanyPickerProps) => {
  const {
    state: { companies },
  } = useIndeedFlexCompaniesContextValue()

  const companiesOptions = useMemo(() => {
    return (
      companies
        ?.slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((company) => ({
          label: `${company.name} (${company.id})`,
          value: company.id,
        })) ?? []
    )
  }, [companies])

  const searcher = useMemo(() => {
    return new Searcher<CompanyOption, FullOptions<CompanyOption>>(
      companiesOptions,
      fuzzyOptions
    )
  }, [companiesOptions])

  const loadOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return true

      const results = searcher.search(
        rawInput,
        fuzzyOptions
      ) as unknown as MatchData<CompanyOption>[]

      return Promise.resolve(results)
    },
    [searcher]
  )

  return (
    <Field label={label} error={error}>
      <ModalAsyncSelect
        block
        isClearable
        isSearchable
        placeholder="Select a company"
        defaultOptions={companiesOptions}
        loadOptions={loadOptions}
        value={
          companiesOptions.find((option) => option.value === value) || null
        }
        onChange={(option) => {
          onChange(option?.value ?? undefined)
        }}
      />
    </Field>
  )
}
